import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppButton,
  Box,
  FlexControlledForm,
  Modal,
  Preloader,
  ServiceConfig,
  ShimmerBox,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { NO_KEY, YES_KEY } from '@/common/constants';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores/useHealthPlanName.store';

import { AMOUNT_TYPE_KEY } from './hooks/usePaymentRuleFields';
import AddPaymentRule from './AddPaymentRule';
import { MainInfo } from './CreateProcessingRule.types';
import {
  useCreateServicePayment,
  useEnhancedServiceTypeOptions,
  usePaymentRules,
  useServicePaymentFields,
} from './hooks';
import PaymentRuleItem from './PaymentRuleItem';
import { usePaymentAccount, useServiceType } from './queries';

const Delimiter = styled(Box)`
  border: none;
  background-color: ${({ theme }) => theme.colors.border1};
  height: 1px;
`;

interface Props {
  visible: boolean;
  setVisible: () => void;
  tierId: string;
  payoutDefinitionId: string;
  updatedElement?: ServiceConfig;
  setUpdatedElement: (value?: ServiceConfig) => void;
  deductibleType?: string;
  healthPlanTypes?: string[];
  serviceConfigs?: ServiceConfig[];
}

const AddServicePaymentModal: React.FC<Props> = ({
  visible,
  setVisible,
  tierId,
  payoutDefinitionId,
  updatedElement,
  setUpdatedElement,
  deductibleType,
  healthPlanTypes,
  serviceConfigs,
}) => {
  const [state, setState] = useState<MainInfo>({
    serviceTypes: [],
    description: '',
    paymentValue: '',
    applyToDeductible: '',
    paymentType: '',
    claimValue: '',
    showEligibleServices: NO_KEY,
  });
  const [errorName, setErrorName] = useState<string[]>([]);
  const [accountArray, setAccountArray] = useState<string[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const prevPaymentType = useRef(state.paymentType);

  const {
    fieldsValues: paymentRulesValues,
    getFieldValuesById,
    handleChangeValues,
    handleRemoveById,
    handleAddNewServicePayment,
    handleClearValues,
    getInitialFieldValuesById,
  } = usePaymentRules(updatedElement?.paymentRules);

  const plans = useHealthPlanNameStore((state) => state.state.plans);

  const { fieldOptions: serviceTypeOptions, isLoading: isServiceLoading } = useServiceType(
    visible,
    payoutDefinitionId,
    healthPlanTypes,
    deductibleType,
    state.showEligibleServices === YES_KEY ? plans : undefined,
    true
  );

  const { enhancedServiceTypeOptions, unusedServiceIds } = useEnhancedServiceTypeOptions(
    serviceTypeOptions,
    serviceConfigs
  );
  const { fieldOptions: paymentAccounts, isLoading: isPaymentLoading } = usePaymentAccount(
    visible,
    payoutDefinitionId
  );

  const fields = useServicePaymentFields(
    state,
    enhancedServiceTypeOptions,
    {
      serviceTypes: errorName,
    },
    !!plans?.length,
    unusedServiceIds
  );
  const handleCloseModal = useCallback(() => {
    setVisible();
    setErrorName([]);
    setUpdatedElement(undefined);
    setState({
      serviceTypes: [],
      description: '',
      paymentValue: '',
      applyToDeductible: '',
      paymentType: '',
      claimValue: '',
      showEligibleServices: NO_KEY,
    });
  }, [setVisible, setUpdatedElement]);

  const { isSubmitted, isLoading, handleSubmit, isServiceTypeError } = useCreateServicePayment({
    mainValue: state,
    tierId,
    visible,
    paymentRules: paymentRulesValues,
    onClose: handleCloseModal,
    isUpdateMode: !!updatedElement,
    updatedConfigId: updatedElement?.id,
  });

  const getAccountValues = useCallback(() => {
    const newArray = paymentRulesValues?.map((item) => item?.account);
    setAccountArray(newArray);
  }, [paymentRulesValues]);

  useEffect(() => {
    getAccountValues();
  }, [paymentRulesValues, getAccountValues]);

  useEffect(() => {
    if (state.paymentType && prevPaymentType.current !== state.paymentType) {
      handleClearValues(AMOUNT_TYPE_KEY);
      prevPaymentType.current = state.paymentType;
    } else if (state.paymentType) {
      prevPaymentType.current = state.paymentType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paymentType]);

  useEffect(() => {
    if (isServiceTypeError) {
      setErrorName(state.serviceTypes);
    } else {
      setErrorName([]);
    }
  }, [isServiceTypeError, state.serviceTypes]);

  useEffect(() => {
    if (updatedElement) {
      const servicesIds = updatedElement.services?.map((service) => service.id) || [];

      setState({
        serviceTypes: servicesIds,
        description: updatedElement.description,
        paymentValue: updatedElement.claimValue ? YES_KEY : NO_KEY,
        applyToDeductible: updatedElement.isAppliedDeductible ? YES_KEY : NO_KEY,
        paymentType: updatedElement.paymentType,
        claimValue: _.toString(updatedElement.claimValue),
        showEligibleServices: NO_KEY,
      });
    }
  }, [updatedElement]);

  const isCurrentLoading = useMemo(
    () => isServiceLoading || isPaymentLoading,
    [isServiceLoading, isPaymentLoading]
  );

  useEffect(() => {
    if (!initialLoading && state.showEligibleServices === YES_KEY) {
      setState((prevState) => ({
        ...prevState,
        serviceTypes: [],
      }));
    }
  }, [initialLoading, state.showEligibleServices, serviceTypeOptions]);

  useEffect(() => {
    setInitialLoading(true);
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [state.showEligibleServices]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let timer: NodeJS.Timeout;
    if (visible && updatedElement) {
      setInitialLoading(true);
      timer = setTimeout(() => {
        setInitialLoading(false);
      }, 1000);
    } else {
      setInitialLoading(false);
    }
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  return (
    <Modal visible={visible} onSetVisible={handleCloseModal} onClear={handleCloseModal}>
      <Box direction="column" data-testid="add-service-payment-modal-wrapper">
        <Box direction="row" align="center" justify="center" pad={{ bottom: 'spacing24' }}>
          <Text size="2xl" color="textTitle" weight="bold">
            {updatedElement ? 'Edit Service Payment' : 'Service Payment'}
          </Text>
        </Box>
        <Box>
          <Box pad="l" background="module" round="moduleRound">
            {initialLoading || isCurrentLoading ? (
              <ShimmerBox width="100%" height="465px" round="container1Round" margin={{ bottom: 's' }} />
            ) : (
              <Box
                background="canvas"
                pad={{ vertical: 'l' }}
                elevation="default"
                margin={{ bottom: 's' }}
                round="container1Round"
                border={{ color: 'border2', size: 'small' }}
              >
                <FlexControlledForm
                  isModalType
                  fields={fields}
                  editMode
                  // onDirty={setShouldConfirmOnClose}
                  onChangeValues={setState}
                  showError={isSubmitted}
                  wrapperStyle={{ border: 'none' }}
                  formStyle={{ marginBottom: 0, marginTop: 0 }}
                />
              </Box>
            )}
            {paymentRulesValues.map((item, index) => (
              <PaymentRuleItem
                id={item.id}
                index={index}
                isErrorShowed={isSubmitted}
                onRemove={handleRemoveById}
                key={item.id}
                defaultValues={getFieldValuesById(item.id)}
                initialValues={getInitialFieldValuesById(item.id)}
                onChangeValues={handleChangeValues(item.id)}
                paymentType={state.paymentType}
                paymentAccounts={paymentAccounts}
                accountArray={accountArray}
                isLoading={isPaymentLoading}
              />
            ))}
            <Delimiter />
            <Text color="textSecondary" size="medium" weight={400} margin={{ vertical: 's' }}>
              Claims will use the configured processing rules unless a specific payment rule is added.
            </Text>
            <AddPaymentRule onClick={handleAddNewServicePayment} />
          </Box>
          <Box direction="row" align="center" justify="end" pad={{ top: 'l' }}>
            <AppButton buttonType="secondary" width="control" onClick={handleCloseModal}>
              Cancel
            </AppButton>
            <Box margin={{ left: 's' }}>
              <AppButton width="control" onClick={handleSubmit} disabled={isLoading}>
                {isLoading && <Preloader color="white" />}
                {!isLoading && updatedElement ? 'Save' : 'Add'}
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddServicePaymentModal;
